/**
 * What the Mountain Brook permit form is filled from (ADR 0289).
 */

import {
  buildPermitApplication,
  centralDate,
  permitDateDefaults,
  type Job,
  type PermitApplication,
} from "@roof-hawk/core";

import { fetchCompanySettings, fetchJobBalanceInput } from "./queries";
import { supabase, unwrap } from "./supabase";

interface PermitContact {
  full_name: string | null;
  phone: string | null;
}

/**
 * The pink sheet, filled from the job, before the office changes any of it.
 *
 * The contract is what the customer signed, with no change order signed since:
 * `fetchJobBalanceInput`'s `signedContractCents`, the figure the deposit is
 * half of. Jonathon, 2026-10-17: "original signed contract only. rep needs to
 * be able to edit it though" (ADR 0289). The box stays editable. The dates are
 * the live work orders' booking. Lysa is reached through
 * `company_settings.permit_contact_profile_id`, so who is on the form is a
 * row edit, the way the claim contact is.
 */
export async function fetchPermitApplication(
  job: Job,
): Promise<PermitApplication> {
  const [balance, company, orders, contact, estimates] = await Promise.all([
    fetchJobBalanceInput(job),
    fetchCompanySettings(),
    supabase
      .from("work_orders")
      .select("start_at,end_at")
      .eq("job_id", job.id)
      .eq("is_live", true),
    supabase
      .from("company_settings")
      .select(
        "permit_contact:profiles!company_settings_permit_contact_profile_id_fkey(full_name,phone)",
      )
      .eq("id", 1)
      .maybeSingle(),
    // A new build ticks "New Construction" in place of "Repair".
    supabase
      .from("estimates")
      .select("trade")
      .eq("job_id", job.id)
      .eq("is_active", true),
  ]);
  // The contact is a nicety, not the form: before the migration that adds
  // `permit_contact_profile_id` reaches a database, PostgREST refuses the
  // embed, and the sheet still opens with the contact boxes left to type.
  const signer = contact.error
    ? null
    : ((contact.data as { permit_contact: PermitContact | null } | null)
        ?.permit_contact ?? null);
  const dates = permitDateDefaults(
    job,
    unwrap<{ start_at: string | null; end_at: string | null }[]>(orders),
  );
  return buildPermitApplication({
    today: centralDate(new Date()),
    job,
    contact: job.contact ?? null,
    company,
    signer,
    proposedStart: dates.start,
    proposedEnd: dates.end,
    contractAmount: balance.signedContractCents ?? balance.contract,
    trades: unwrap<{ trade: string | null }[]>(estimates).map(
      (row) => row.trade,
    ),
  });
}
